import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Phone, Mail, MapPin, Calendar, PenTool, Plus, X, Edit3, Camera, Upload, Globe } from 'lucide-react';
import api from '../components/api';
import { toast } from 'react-toastify';
import useGlobale from '../components/global';
import Sidebar from '../components/SideBar';
import Header from '../components/Header';
import DemandeurMenu from '../components/DemandeurMenu';
import countryList from 'react-select-country-list';

const ProfilePage = () => {
    const { user, setUser } = useGlobale();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [profile, setProfile] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [photoLoading, setPhotoLoading] = useState(false);
    const [showPhotoModal, setShowPhotoModal] = useState(false);
    const [selectedPhoto, setSelectedPhoto] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(null);
    const [formData, setFormData] = useState({
        genre: '',
        date_naissance: '',
        lieu_naissance: '',
        nationalite: 'NE', // Niger ISO code
        situation_familiale: '',
        telephone2: '',
        adresse: '',
        lien_linkdin: '',
        titre: '',
        apropos: '',
    });

    // Get country options from react-select-country-list
    const countryOptions = useMemo(() => countryList().getData(), []);

    // État pour suivre si le formulaire contient des erreurs
    const [errors, setErrors] = useState({});

    // Set sidebar initial state based on screen size
    useEffect(() => {
        const handleResize = () => {
            setIsSidebarOpen(window.innerWidth >= 1024);
        };
        
        // Set initial state
        handleResize();
        
        // Add event listener
        window.addEventListener('resize', handleResize);
        
        // Cleanup
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        fetchProfile();
    }, []);

    const fetchProfile = async () => {
        setLoading(true);
        try {
            const response = await api.get('demandeur/profiles/');
            if (response.data && response.data.length > 0) {
                setProfile(response.data[0]);
                // Pré-remplir le formulaire pour l'édition avec les données du profil
                setFormData({
                    genre: response.data[0].genre || '',
                    date_naissance: response.data[0].date_naissance ? response.data[0].date_naissance.split('T')[0] : '',
                    lieu_naissance: response.data[0].lieu_naissance || '',
                    nationalite: response.data[0].nationalite || 'NE',
                    situation_familiale: response.data[0].situation_familiale || '',
                    telephone2: response.data[0].telephone2 || '',
                    adresse: response.data[0].adresse || '',
                    lien_linkdin: response.data[0].lien_linkdin || '',
                    titre: response.data[0].titre || '',
                    apropos: response.data[0].apropos || '',
                });
            }
        } catch (error) {
            console.error('Erreur lors de la récupération du profil:', error);
            if (error.response && error.response.status === 404) {
                toast.info('Veuillez créer votre profil pour continuer');
            } else {
                toast.error('Erreur lors de la récupération de votre profil');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
        
        // Effacer l'erreur lorsqu'un champ est modifié
        if (errors[name]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.genre) {
            newErrors.genre = "Le genre est requis";
        }
        
        if (!formData.date_naissance) {
            newErrors.date_naissance = "La date de naissance est requise";
        }
        
        if (!formData.lieu_naissance.trim()) {
            newErrors.lieu_naissance = "Le lieu de naissance est requis";
        }
        
        if (!formData.nationalite) {
            newErrors.nationalite = "La nationalité est requise";
        }
        
        if (!formData.adresse.trim()) {
            newErrors.adresse = "L'adresse est requise";
        }
        
        // Validation de l'URL LinkedIn
        if (formData.lien_linkdin) {
            const linkedinRegex = /^https?:\/\/(www\.)?linkedin\.com\/in\/[\w-]+\/?$/;
            if (!linkedinRegex.test(formData.lien_linkdin)) {
                newErrors.lien_linkdin = "Veuillez entrer une URL LinkedIn valide (ex: https://www.linkedin.com/in/votre-profil)";
            }
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }
        
        setLoading(true);
        try {
            let response;
            if (isEditMode && profile) {
                response = await api.put(`demandeur/profiles/${profile.id}/`, {
                    ...formData,
                    utlisateur: user.id,
                });
                toast.success('Profil mis à jour avec succès');
            } else {
                response = await api.post('demandeur/profiles/', {
                    ...formData,
                    utlisateur: user.id,
                });
                toast.success('Profil créé avec succès');
            }
            
            setProfile(response.data);
            setShowModal(false);
        } catch (error) {
            console.error('Erreur lors de la sauvegarde du profil:', error);
            
            if (error.response && error.response.data) {
                if (typeof error.response.data === 'object') {
                    setErrors(error.response.data);
                } else {
                    toast.error('Erreur lors de la sauvegarde du profil');
                }
            } else {
                toast.error('Erreur de connexion au serveur');
            }
        } finally {
            setLoading(false);
        }
    };

    const openEditModal = () => {
        setIsEditMode(true);
        setShowModal(true);
    };

    const openAddModal = () => {
        setIsEditMode(false);
        setFormData({
            genre: '',
            date_naissance: '',
            lieu_naissance: '',
            nationalite: 'NE', // Niger ISO code
            situation_familiale: '',
            telephone2: '',
            adresse: '',
            lien_linkdin: '',
        });
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setErrors({});
    };

    // Fonction pour formater le genre pour l'affichage
    const formatGenre = (genre) => {
        const genres = {
            'monsieur': 'Monsieur',
            'madame': 'Madame',
            'mademoiselle': 'Mademoiselle'
        };
        return genres[genre] || genre;
    };

    // Fonction pour formater la situation familiale pour l'affichage
    const formatSituationFamiliale = (situation) => {
        const situations = {
            'celibataire': 'Célibataire',
            'marié': 'Marié(e)',
            'divorcé': 'Divorcé(e)',
            'veuf': 'Veuf/Veuve'
        };
        return situations[situation] || situation;
    };

    // Fonction pour obtenir le nom complet du pays à partir du code ISO
    const getCountryNameFromCode = (code) => {
        const country = countryOptions.find(country => country.value === code);
        return country ? country.label : code;
    };

    // Fonction pour afficher une photo de profil ou une image par défaut
    const [photoError, setPhotoError] = useState(false);
    
    const renderProfilePhoto = () => {
        if (user && user.photo_url && !photoError) {
            return (
                <img 
                    src={user.photo_url} 
                    alt={`${user?.first_name} ${user?.last_name}`} 
                    className="h-full w-full object-cover rounded-full"
                    onError={() => setPhotoError(true)}
                />
            );
        }
        
        return (
            <div className="h-full w-full flex items-center justify-center bg-orange-100 text-orange-500 text-2xl font-bold rounded-full">
                {user?.first_name?.charAt(0) || ''}{user?.last_name?.charAt(0) || ''}
            </div>
        );
    };
    
    const handlePhotoChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        
        // Vérifier le type de fichier
        if (!file.type.match('image.*')) {
            toast.error('Veuillez sélectionner une image valide');
            return;
        }
        
        // Vérifier la taille du fichier (5 MB max)
        if (file.size > 5 * 1024 * 1024) {
            toast.error('La taille de l\'image ne doit pas dépasser 5 MB');
            return;
        }
        
        setSelectedPhoto(file);
        
        // Créer une URL pour prévisualiser l'image
        const reader = new FileReader();
        reader.onloadend = () => {
            setPhotoPreview(reader.result);
        };
        reader.readAsDataURL(file);
    };
    
    const uploadPhoto = async () => {
        if (!selectedPhoto) {
            toast.error('Veuillez sélectionner une photo');
            return;
        }
        
        setPhotoLoading(true);
        
        try {
            const formData = new FormData();
            formData.append('photo', selectedPhoto);
            
            const response = await api.post('utilisateur/update-photo/', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            
            setUser(response.data);
            setPhotoError(false);
            
            // Fermer le modal
            setShowPhotoModal(false);
            setSelectedPhoto(null);
            setPhotoPreview(null);
            
            toast.success('Photo de profil mise à jour avec succès');
        } catch (error) {
            console.error('Erreur lors de la mise à jour de la photo:', error);
            toast.error('Erreur lors de la mise à jour de la photo');
        } finally {
            setPhotoLoading(false);
        }
    };
    
    const openPhotoModal = () => {
        setShowPhotoModal(true);
    };
    
    const closePhotoModal = () => {
        setShowPhotoModal(false);
        setSelectedPhoto(null);
        setPhotoPreview(null);
    };

    const renderContent = () => {
        if (loading && !profile) {
            return (
                <div className="flex justify-center items-center h-full">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-orange-500"></div>
                </div>
            );
        }

        return (
            <>
                <div className="mb-8">
                    <h1 className="text-2xl font-bold text-[#437011]">
                        Mon Profil
                    </h1>
                    <p className="text-gray-600">Gérez vos informations personnelles et professionnelles</p>
                </div>

                {profile ? (
                    <div className="bg-white rounded-xl shadow-md overflow-hidden">
                        {/* En-tête du profil */}
                        <div className="bg-gradient-to-r from-orange-500 to-orange-600 p-8">
                            <div className="flex flex-col md:flex-row md:items-start justify-between">
                                <div className="flex flex-col md:flex-row items-center mb-4 md:mb-0">
                                    <div className="h-28 w-28 rounded-full bg-white flex items-center justify-center text-orange-500 shadow-md border-4 border-white overflow-hidden relative group">
                                        {renderProfilePhoto()}
                                        <button 
                                            onClick={openPhotoModal}
                                            className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white rounded-full"
                                        >
                                            <Camera size={24} />
                                        </button>
                                    </div>
                                    <div className="ml-0 md:ml-6 mt-4 md:mt-0 text-center md:text-left text-white">
                                        <h2 className="text-3xl font-bold">{formatGenre(profile.genre)} {user?.first_name} {user?.last_name}</h2>
                                        <p className="text-orange-100 text-lg mt-1">Chercheur d'emploi</p>
                                    </div>
                                </div>
                                <button 
                                    onClick={openEditModal}
                                    className="bg-white text-orange-600 py-2 px-6 rounded-lg flex items-center hover:bg-orange-50 transition shadow-md font-medium ml-auto md:ml-0"
                                >
                                    <Edit3 size={18} className="mr-2" />
                                    Modifier
                                </button>
                            </div>
                        </div>

                        {/* Informations personnelles */}
                        <div className="p-6 md:p-8">
                            <h3 className="text-xl font-semibold text-gray-800 mb-6 pb-2 border-b border-gray-200">Informations personnelles</h3>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div className="bg-orange-50 rounded-lg p-5 transform transition-transform duration-300 hover:shadow-md hover:-translate-y-1">
                                    <div className="flex items-start">
                                        <Mail className="h-6 w-6 text-orange-500 mt-1 mr-3" />
                                        <div>
                                            <p className="text-sm font-medium text-gray-500 mb-1">Email</p>
                                            <p className="text-gray-800 font-medium">{user?.email || "Non renseigné"}</p>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="bg-orange-50 rounded-lg p-5 transform transition-transform duration-300 hover:shadow-md hover:-translate-y-1">
                                    <div className="flex items-start">
                                        <Phone className="h-6 w-6 text-orange-500 mt-1 mr-3" />
                                        <div>
                                            <p className="text-sm font-medium text-gray-500 mb-1">Téléphone principal</p>
                                            <p className="text-gray-800 font-medium">{user?.telephone || "Non renseigné"}</p>
                                        </div>
                                    </div>
                                </div>
                                
                                {profile.telephone2 && (
                                    <div className="bg-orange-50 rounded-lg p-5 transform transition-transform duration-300 hover:shadow-md hover:-translate-y-1">
                                        <div className="flex items-start">
                                            <Phone className="h-6 w-6 text-orange-500 mt-1 mr-3" />
                                            <div>
                                                <p className="text-sm font-medium text-gray-500 mb-1">Téléphone secondaire</p>
                                                <p className="text-gray-800 font-medium">{profile.telephone2}</p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                
                                <div className="bg-orange-50 rounded-lg p-5 transform transition-transform duration-300 hover:shadow-md hover:-translate-y-1">
                                    <div className="flex items-start">
                                        <MapPin className="h-6 w-6 text-orange-500 mt-1 mr-3" />
                                        <div>
                                            <p className="text-sm font-medium text-gray-500 mb-1">Adresse</p>
                                            <p className="text-gray-800 font-medium">{profile.adresse || "Non renseignée"}</p>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="bg-orange-50 rounded-lg p-5 transform transition-transform duration-300 hover:shadow-md hover:-translate-y-1">
                                    <div className="flex items-start">
                                        <Calendar className="h-6 w-6 text-orange-500 mt-1 mr-3" />
                                        <div>
                                            <p className="text-sm font-medium text-gray-500 mb-1">Date et lieu de naissance</p>
                                            <p className="text-gray-800 font-medium">
                                                {profile.date_naissance ? new Date(profile.date_naissance).toLocaleDateString('fr-FR') : "Non renseignée"}
                                                {profile.lieu_naissance ? ` à ${profile.lieu_naissance}` : ""}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="bg-orange-50 rounded-lg p-5 transform transition-transform duration-300 hover:shadow-md hover:-translate-y-1">
                                    <div className="flex items-start">
                                        <User className="h-6 w-6 text-orange-500 mt-1 mr-3" />
                                        <div>
                                            <p className="text-sm font-medium text-gray-500 mb-1">Nationalité</p>
                                            <p className="text-gray-800 font-medium">{profile.nationalite ? getCountryNameFromCode(profile.nationalite) : "Non renseignée"}</p>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="bg-orange-50 rounded-lg p-5 transform transition-transform duration-300 hover:shadow-md hover:-translate-y-1">
                                    <div className="flex items-start">
                                        <User className="h-6 w-6 text-orange-500 mt-1 mr-3" />
                                        <div>
                                            <p className="text-sm font-medium text-gray-500 mb-1">Situation matrimoniale</p>
                                            <p className="text-gray-800 font-medium">
                                                {profile.situation_familiale ? formatSituationFamiliale(profile.situation_familiale) : "Non renseignée"}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                
                                {profile.lien_linkdin && (
                                    <div className="bg-orange-50 rounded-lg p-5 transform transition-transform duration-300 hover:shadow-md hover:-translate-y-1">
                                        <div className="flex items-start">
                                            <Globe className="h-6 w-6 text-orange-500 mt-1 mr-3" />
                                            <div>
                                                <p className="text-sm font-medium text-gray-500 mb-1">LinkedIn</p>
                                                <a 
                                                    href={profile.lien_linkdin}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-blue-600 hover:text-blue-800 font-medium underline"
                                                >
                                                    Voir le profil LinkedIn
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="bg-white rounded-xl shadow-md overflow-hidden p-8 text-center">
                        <div className="mb-8">
                            <div className="h-32 w-32 rounded-full bg-orange-100 flex items-center justify-center text-orange-500 mx-auto shadow-inner overflow-hidden relative group">
                                {renderProfilePhoto()}
                                <button 
                                    onClick={openPhotoModal}
                                    className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white rounded-full"
                                >
                                    <Camera size={24} />
                                </button>
                            </div>
                        </div>
                        <h2 className="text-2xl font-semibold text-gray-800 mb-3">Votre profil est vide</h2>
                        <p className="text-gray-600 mb-8 max-w-md mx-auto">
                            Complétez votre profil pour augmenter vos chances de trouver un emploi qui vous correspond.
                        </p>
                        <button
                            onClick={openAddModal}
                            className="bg-orange-600 text-white py-3 px-8 rounded-lg flex items-center justify-center mx-auto hover:bg-orange-700 transition shadow-md font-medium"
                        >
                            <Plus size={20} className="mr-2" />
                            Créer mon profil
                        </button>
                    </div>
                )}
            </>
        );
    };

    return (
        <div className="flex min-h-screen bg-[#F4F8E9]">
            <Sidebar
                isOpen={isSidebarOpen}
                menuItems={DemandeurMenu()}
                onClose={() => setIsSidebarOpen(false)}
            />

            <div className="flex-1 flex flex-col w-full">
                <Header
                    isSidebarOpen={isSidebarOpen}
                    setIsSidebarOpen={setIsSidebarOpen}
                    user={user}
                />
                <main className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8 pb-20 lg:pb-8 w-full">
                    {renderContent()}
                </main>
            </div>

            {/* Modal pour changer la photo de profil */}
            {showPhotoModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
                        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                            <h3 className="text-lg font-semibold text-gray-800">
                                Modifier votre photo de profil
                            </h3>
                            <button
                                onClick={closePhotoModal}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <X size={24} />
                            </button>
                        </div>
                        <div className="p-6">
                            {photoPreview ? (
                                <div className="mb-6">
                                    <div className="h-48 w-48 rounded-full overflow-hidden mx-auto mb-4">
                                        <img 
                                            src={photoPreview} 
                                            alt="Aperçu" 
                                            className="h-full w-full object-cover"
                                        />
                                    </div>
                                    <div className="flex justify-center">
                                        <button
                                            onClick={() => {
                                                setPhotoPreview(null);
                                                setSelectedPhoto(null);
                                            }}
                                            className="text-red-500 hover:text-red-700 text-sm flex items-center"
                                        >
                                            <X size={16} className="mr-1" />
                                            Supprimer cette photo
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="mb-6">
                                    <div className="h-48 w-48 rounded-full bg-gray-100 flex items-center justify-center mx-auto border-2 border-dashed border-gray-300">
                                        <div className="text-center p-4">
                                            <Upload size={36} className="mx-auto text-gray-400" />
                                            <p className="mt-2 text-sm text-gray-500">
                                                Cliquez pour sélectionner une photo
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                            
                            <div className="relative overflow-hidden inline-block w-full">
                                <button
                                    type="button"
                                    className="w-full py-3 bg-orange-100 text-orange-600 rounded-lg hover:bg-orange-200 transition font-medium"
                                >
                                    {photoPreview ? "Changer de photo" : "Sélectionner une photo"}
                                </button>
                                <input
                                    type="file"
                                    name="photo"
                                    onChange={handlePhotoChange}
                                    accept="image/*"
                                    className="absolute inset-0 opacity-0 cursor-pointer"
                                />
                            </div>
                            
                            <div className="mt-4">
                                <button
                                    onClick={uploadPhoto}
                                    disabled={!selectedPhoto || photoLoading}
                                    className={`w-full py-3 rounded-lg text-white ${!selectedPhoto || photoLoading ? 'bg-gray-300 cursor-not-allowed' : 'bg-orange-600 hover:bg-orange-700'} transition font-medium`}
                                >
                                    {photoLoading ? (
                                        <span className="flex items-center justify-center">
                                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Téléchargement...
                                        </span>
                                    ) : "Enregistrer la photo"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal pour ajouter/éditer un profil */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                            <h3 className="text-lg font-semibold text-gray-800">
                                {isEditMode ? "Modifier mon profil" : "Créer mon profil"}
                            </h3>
                            <button
                                onClick={closeModal}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <X size={24} />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} className="p-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Civilité<span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        name="genre"
                                        value={formData.genre}
                                        onChange={handleInputChange}
                                        className={`w-full px-4 py-2 border ${errors.genre ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500`}
                                    >
                                        <option value="">Sélectionnez votre genre</option>
                                        <option value="monsieur">Monsieur</option>
                                        <option value="madame">Madame</option>
                                        <option value="mademoiselle">Mademoiselle</option>
                                    </select>
                                    {errors.genre && <p className="mt-1 text-sm text-red-600">{errors.genre}</p>}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Téléphone secondaire
                                    </label>
                                    <input
                                        type="tel"
                                        name="telephone2"
                                        value={formData.telephone2}
                                        onChange={handleInputChange}
                                        className={`w-full px-4 py-2 border ${errors.telephone2 ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500`}
                                        placeholder="Numéro de téléphone secondaire"
                                    />
                                    {errors.telephone2 && <p className="mt-1 text-sm text-red-600">{errors.telephone2}</p>}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Adresse <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="adresse"
                                        value={formData.adresse}
                                        onChange={handleInputChange}
                                        className={`w-full px-4 py-2 border ${errors.adresse ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500`}
                                        placeholder="Votre adresse complète"
                                    />
                                    {errors.adresse && <p className="mt-1 text-sm text-red-600">{errors.adresse}</p>}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Date de naissance <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="date"
                                        name="date_naissance"
                                        value={formData.date_naissance}
                                        onChange={handleInputChange}
                                        className={`w-full px-4 py-2 border ${errors.date_naissance ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500`}
                                    />
                                    {errors.date_naissance && <p className="mt-1 text-sm text-red-600">{errors.date_naissance}</p>}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Lieu de naissance <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="lieu_naissance"
                                        value={formData.lieu_naissance}
                                        onChange={handleInputChange}
                                        className={`w-full px-4 py-2 border ${errors.lieu_naissance ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500`}
                                        placeholder="Ville ou lieu de naissance"
                                    />
                                    {errors.lieu_naissance && <p className="mt-1 text-sm text-red-600">{errors.lieu_naissance}</p>}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Nationalité <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        name="nationalite"
                                        value={formData.nationalite}
                                        onChange={handleInputChange}
                                        className={`w-full px-4 py-2 border ${errors.nationalite ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500`}
                                    >
                                        <option value="">Sélectionnez votre nationalité</option>
                                        {countryOptions.map(country => (
                                            <option key={country.value} value={country.value}>
                                                {country.label}
                                            </option>
                                        ))}
                                    </select>
                                    {errors.nationalite && <p className="mt-1 text-sm text-red-600">{errors.nationalite}</p>}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Situation familiale
                                    </label>
                                    <select
                                        name="situation_familiale"
                                        value={formData.situation_familiale}
                                        onChange={handleInputChange}
                                        className={`w-full px-4 py-2 border ${errors.situation_familiale ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500`}
                                    >
                                        <option value="">Sélectionnez votre situation familiale</option>
                                        <option value="celibataire">Célibataire</option>
                                        <option value="marié">Marié(e)</option>
                                        <option value="divorcé">Divorcé(e)</option>
                                        <option value="veuf">Veuf/Veuve</option>
                                    </select>
                                    {errors.situation_familiale && <p className="mt-1 text-sm text-red-600">{errors.situation_familiale}</p>}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Profil LinkedIn
                                    </label>
                                    <input
                                        type="url"
                                        name="lien_linkdin"
                                        value={formData.lien_linkdin}
                                        onChange={handleInputChange}
                                        className={`w-full px-4 py-2 border ${errors.lien_linkdin ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500`}
                                        placeholder="https://www.linkedin.com/in/votre-profil"
                                    />
                                    {errors.lien_linkdin && <p className="mt-1 text-sm text-red-600">{errors.lien_linkdin}</p>}
                                </div>
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Titre professionnel
                                    </label>
                                    <input
                                        type="text"
                                        name="titre"
                                        value={formData.titre}
                                        onChange={handleInputChange}
                                        className={`w-full px-4 py-2 border ${errors.titre ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500`}
                                        placeholder="Ex: Développeur Web Full Stack, Chef de projet, etc."
                                    />
                                    {errors.titre && <p className="mt-1 text-sm text-red-600">{errors.titre}</p>}
                                </div>
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        À propos de moi
                                    </label>
                                    <textarea
                                        name="apropos"
                                        value={formData.apropos}
                                        onChange={handleInputChange}
                                        rows="4"
                                        className={`w-full px-4 py-2 border ${errors.apropos ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500`}
                                        placeholder="Décrivez votre parcours, vos compétences clés et ce que vous recherchez..."
                                    />
                                    {errors.apropos && <p className="mt-1 text-sm text-red-600">{errors.apropos}</p>}
                                </div>
                            </div>
                            <div className="mt-6 flex justify-end space-x-3">
                                <button
                                    type="button"
                                    onClick={closeModal}
                                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                                >
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className={`px-4 py-2 rounded-lg text-white ${loading ? 'bg-orange-400' : 'bg-orange-600 hover:bg-orange-700'}`}
                                >
                                    {loading ? (
                                        <span className="flex items-center">
                                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Enregistrement...
                                        </span>
                                    ) : isEditMode ? "Mettre à jour" : "Créer mon profil"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProfilePage;

